import { useState, useMemo, useEffect } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import { toast } from 'sonner'
import { Plus, Trash2, Copy, X, Link2, Pencil, ChevronDown, FileText, Users, Monitor, Upload, Check, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible'
import {
  useFbProfiles,
  useCreateFbProfile,
  useUpdateFbProfile,
  useDeleteFbProfile,
  useFbPages,
  useCreateFbPage,
  useUpdateFbPage,
  useDeleteFbPage,
  useFbGroups,
  useCreateFbGroup,
  useImportFbGroups,
  useUpdateFbGroup,
  useDeleteFbGroup,
  useGenerateExtToken,
  useRevokeExtToken,
  usePageGroupIds,
  useSyncPageGroups,
} from '@/api/hooks/useExtension'
import { useFinancePages } from '@/api/hooks/usePages'
import { timeAgo } from './shared'

interface ProfileSidebarProps {
  selectedProfileIds: string[]
  onSelectionChange: (ids: string[]) => void
}

export function ProfileSidebar({ selectedProfileIds, onSelectionChange }: ProfileSidebarProps) {
  const { t } = useLanguage()
  const { data: profilesData, isLoading } = useFbProfiles()
  const { data: pagesData } = useFbPages()
  const { data: groupsData } = useFbGroups()
  const { data: financePages } = useFinancePages()
  const createProfile = useCreateFbProfile()
  const updateProfile = useUpdateFbProfile()
  const deleteProfile = useDeleteFbProfile()
  const generateToken = useGenerateExtToken()
  const revokeToken = useRevokeExtToken()
  const createPage = useCreateFbPage()
  const updatePage = useUpdateFbPage()
  const deletePage = useDeleteFbPage()
  const createGroup = useCreateFbGroup()
  const importGroups = useImportFbGroups()
  const updateGroup = useUpdateFbGroup()
  const deleteGroup = useDeleteFbGroup()

  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [renameId, setRenameId] = useState<string | null>(null)
  const [renameName, setRenameName] = useState('')
  const [generatedLinks, setGeneratedLinks] = useState<Record<string, string>>({})
  const [importOpen, setImportOpen] = useState(false)
  const [importText, setImportText] = useState('')
  const [importCollection, setImportCollection] = useState('')
  const [importProfileId, setImportProfileId] = useState('')
  // Link page dialog (multi-select)
  const [linkPageOpen, setLinkPageOpen] = useState(false)
  const [linkPageProfileId, setLinkPageProfileId] = useState('')
  const [linkPageFinanceIds, setLinkPageFinanceIds] = useState<string[]>([])
  const [linkingPages, setLinkingPages] = useState(false)
  // Page-group linking dialog
  const [linkGroupsOpen, setLinkGroupsOpen] = useState(false)
  const [linkGroupsPageId, setLinkGroupsPageId] = useState<string | null>(null)
  const [linkGroupsProfileId, setLinkGroupsProfileId] = useState<string | null>(null)
  const [linkGroupsSelected, setLinkGroupsSelected] = useState<string[]>([])
  const syncPageGroups = useSyncPageGroups()
  const { data: currentPageGroupIds, isLoading: loadingPageGroups } = usePageGroupIds(linkGroupsPageId)
  // Search
  const [profileSearch, setProfileSearch] = useState('')

  const profiles = profilesData?.profiles ?? []
  const allPages = pagesData?.pages ?? []
  const allGroups = groupsData?.groups ?? []
  const availableFinancePages = financePages ?? []

  // Group pages and groups by profile
  const profileTree = useMemo(() => {
    return profiles.map(p => ({
      profile: p,
      pages: allPages.filter(pg => pg.profileId === p.id),
      groups: allGroups.filter(g => g.profileId === p.id),
    }))
  }, [profiles, allPages, allGroups])

  const unassignedGroups = useMemo(() => allGroups.filter(g => !g.profileId), [allGroups])

  const linkedPageIds = new Set(allPages.map(p => p.fbPageId))
  const unlinkedFinancePages = availableFinancePages.filter(fp => !linkedPageIds.has(fp.fbPageId || ''))

  const toggleProfile = (id: string) => {
    onSelectionChange(
      selectedProfileIds.includes(id)
        ? selectedProfileIds.filter(x => x !== id)
        : [...selectedProfileIds, id]
    )
  }

  const toggleAll = () => {
    if (selectedProfileIds.length === profiles.length) {
      onSelectionChange([])
    } else {
      onSelectionChange(profiles.map(p => p.id))
    }
  }

  const handleCreate = () => {
    if (!newName.trim()) return
    createProfile.mutate({ name: newName.trim() }, {
      onSuccess: () => { toast.success(t('Perfil criado!')); setCreateOpen(false); setNewName('') },
      onError: (e) => toast.error(e.message),
    })
  }

  const handleRename = () => {
    if (!renameId || !renameName.trim()) return
    updateProfile.mutate({ id: renameId, name: renameName.trim() }, {
      onSuccess: () => { toast.success(t('Renomeado!')); setRenameId(null) },
    })
  }

  const handleGenerateLink = (profileId: string) => {
    generateToken.mutate({ profile_id: profileId }, {
      onSuccess: (r) => {
        setGeneratedLinks(prev => ({ ...prev, [profileId]: r.setupUrl }))
        toast.success(t('Link gerado!'))
      },
      onError: (e) => toast.error(e.message),
    })
  }

  const handleRevoke = (profileId: string) => {
    if (!confirm(t('Revogar token? A extensao deste perfil sera desconectada.'))) return
    revokeToken.mutate({ profile_id: profileId }, {
      onSuccess: () => {
        setGeneratedLinks(prev => { const n = { ...prev }; delete n[profileId]; return n })
        toast.success(t('Token revogado!'))
      },
    })
  }

  const handleLinkPages = async () => {
    if (!linkPageProfileId || linkPageFinanceIds.length === 0) return
    setLinkingPages(true)
    let count = 0
    for (const fpId of linkPageFinanceIds) {
      const fp = availableFinancePages.find(p => p.id === fpId)
      if (!fp) continue
      try {
        await createPage.mutateAsync(
          { profile_id: linkPageProfileId, fb_page_id: fp.fbPageId || fp.id, name: fp.name, url: fp.link || undefined },
        )
        count++
      } catch (e: any) {
        toast.error(`${fp.name}: ${e.message}`)
      }
    }
    setLinkingPages(false)
    if (count > 0) {
      toast.success(t(`${count} pagina(s) vinculada(s)!`))
      setLinkPageOpen(false); setLinkPageFinanceIds([]); setLinkPageProfileId('')
    }
  }

  const toggleLinkPage = (id: string) => {
    setLinkPageFinanceIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const handleImport = () => {
    const urls = importText.split('\n').map(l => l.trim()).filter(Boolean)
    if (urls.length === 0) return
    const groups = urls.map(url => ({ url, name: '', fb_group_id: '' }))
    importGroups.mutate(
      { groups, collection: importCollection.trim() || undefined, profile_id: importProfileId || undefined },
      {
        onSuccess: (r) => {
          toast.success(`${r.added} grupos adicionados, ${r.skipped} duplicados`)
          setImportOpen(false); setImportText(''); setImportProfileId('')
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }

  // Groups for the currently selected page in the link dialog
  const linkGroupsProfileGroups = useMemo(() => {
    if (!linkGroupsProfileId) return []
    return allGroups.filter(g => g.profileId === linkGroupsProfileId)
  }, [allGroups, linkGroupsProfileId])

  // Initialize checkbox selection when page group data loads
  useEffect(() => {
    if (linkGroupsOpen && currentPageGroupIds && !loadingPageGroups) {
      setLinkGroupsSelected(currentPageGroupIds)
    }
  }, [linkGroupsOpen, currentPageGroupIds, loadingPageGroups])

  const handleOpenLinkGroups = (pageId: string, profileId: string) => {
    setLinkGroupsPageId(pageId)
    setLinkGroupsProfileId(profileId)
    setLinkGroupsSelected([])
    setLinkGroupsOpen(true)
  }

  const handleCloseLinkGroups = () => {
    setLinkGroupsOpen(false)
    setLinkGroupsPageId(null)
    setLinkGroupsProfileId(null)
    setLinkGroupsSelected([])
  }

  const handleSaveLinkGroups = () => {
    if (!linkGroupsPageId) return
    syncPageGroups.mutate(
      { pageId: linkGroupsPageId, groupIds: linkGroupsSelected },
      {
        onSuccess: () => {
          toast.success(t('Grupos vinculados com sucesso!'))
          handleCloseLinkGroups()
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }

  const toggleLinkGroup = (groupId: string) => {
    setLinkGroupsSelected(prev =>
      prev.includes(groupId) ? prev.filter(x => x !== groupId) : [...prev, groupId],
    )
  }

  if (isLoading) return <Skeleton className="h-96 w-full rounded-2xl" />

  return (
    <div className="space-y-3">
      {/* Actions bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={() => setCreateOpen(true)} className="rounded-lg gap-1.5 text-xs">
          <Plus className="h-3 w-3" />{t('Perfil')}
        </Button>
        <Button size="sm" variant="outline" className="rounded-lg border-border/40 gap-1.5 text-xs" onClick={() => setImportOpen(true)}>
          <Upload className="h-3 w-3" />{t('Import Grupos')}
        </Button>
        <Button size="sm" variant="outline" className="rounded-lg border-border/40 gap-1.5 text-xs" onClick={() => setLinkPageOpen(true)}>
          <Link2 className="h-3 w-3" />{t('Vincular Pagina')}
        </Button>
      </div>

      {/* Search + select all */}
      {profiles.length > 0 && (
        <div className="space-y-2">
          {profiles.length > 4 && (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                value={profileSearch}
                onChange={e => setProfileSearch(e.target.value)}
                placeholder={t(`Buscar ${profiles.length} perfis...`)}
                className="h-7 pl-8 text-xs rounded-lg border-border/40 bg-muted/30 border-transparent focus:border-primary/30 focus:bg-transparent transition-colors"
              />
              {profileSearch && (
                <button className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" onClick={() => setProfileSearch('')}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 px-1">
            <Checkbox
              checked={selectedProfileIds.length === profiles.length && profiles.length > 0}
              onCheckedChange={toggleAll}
            />
            <span className="text-[11px] text-muted-foreground">{t('Selecionar todos')} ({profiles.length})</span>
          </div>
        </div>
      )}

      {/* Profile tree */}
      {profiles.length === 0 ? (
        <div className="rounded-2xl border border-border/40 bg-card/50 p-6 text-center">
          <Monitor className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">{t('Nenhum perfil criado.')}</p>
          <p className="text-[10px] text-muted-foreground mt-1">{t('Crie um perfil para cada conta Facebook.')}</p>
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[calc(100vh-260px)] overflow-y-auto pr-1">
          {profileTree
            .filter(({ profile: p }) => !profileSearch || p.name.toLowerCase().includes(profileSearch.toLowerCase()))
            .map(({ profile: p, pages, groups }) => (
            <Collapsible key={p.id}>
              <div className={`rounded-xl border transition-all duration-200 ${selectedProfileIds.includes(p.id) ? 'border-primary/40 bg-primary/5' : 'border-border/40 bg-card/50'}`}>
                {/* Profile header */}
                <div className="flex items-center gap-2 px-3 py-2">
                  <Checkbox
                    checked={selectedProfileIds.includes(p.id)}
                    onCheckedChange={() => toggleProfile(p.id)}
                  />
                  <span className={`w-2 h-2 rounded-full shrink-0 ${p.isOnline ? 'bg-green-500 animate-pulse' : 'bg-zinc-500'}`} />
                  <CollapsibleTrigger className="flex items-center gap-1.5 flex-1 min-w-0 text-left">
                    <span className="text-xs font-semibold truncate">{p.name}</span>
                    <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </CollapsibleTrigger>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <Button variant="ghost" size="icon" className="h-6 w-6 rounded-md text-muted-foreground hover:text-foreground" onClick={() => handleGenerateLink(p.id)} title={t('Gerar Link')}>
                      <Link2 className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 rounded-md text-muted-foreground hover:text-foreground" onClick={() => { setRenameId(p.id); setRenameName(p.name) }} title={t('Renomear')}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 rounded-md text-muted-foreground hover:text-destructive" onClick={() => { if (confirm(t('Excluir perfil e desconectar?'))) deleteProfile.mutate(p.id) }} title={t('Excluir')}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Generated link inline */}
                {generatedLinks[p.id] && (
                  <div className="mx-3 mb-2 bg-emerald-500/10 border border-emerald-500/25 rounded-lg p-2 space-y-1">
                    <div className="flex gap-1.5">
                      <code className="flex-1 bg-muted/40 px-2 py-1 rounded text-[9px] break-all">{generatedLinks[p.id]}</code>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 shrink-0" onClick={() => { navigator.clipboard.writeText(generatedLinks[p.id]); toast.success(t('Copiado!')) }}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <p className="text-[9px] text-muted-foreground">{t('Abra no Chrome Profile desejado')}</p>
                  </div>
                )}

                {/* Info line */}
                <div className="flex items-center gap-3 px-3 pb-1 text-[10px] text-muted-foreground flex-wrap">
                  <span>{p.isOnline ? t('Online') : `${t('Offline')} ${timeAgo(p.lastHeartbeatAt, t)}`}</span>
                  <span>{groups.length} {t('grupos')}</span>
                  {pages.length > 0 && <span>{pages.length} {t('pag.')}</span>}
                  {p.hasToken && (
                    <button className="text-destructive hover:underline" onClick={() => handleRevoke(p.id)}>{t('Revogar token')}</button>
                  )}
                </div>

                <CollapsibleContent>
                  <div className="border-t border-border/30 px-3 py-2 space-y-2">
                    {/* Pages */}
                    {pages.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('Paginas')}</p>
                        {pages.map(pg => {
                          const isDefault = p.defaultPageId === pg.id
                          return (
                            <div key={pg.id} className="flex items-center gap-2 py-0.5 group">
                              <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                              <span className="text-[11px] truncate flex-1">{pg.name}</span>
                              <button
                                onClick={() => updateProfile.mutate({ id: p.id, default_page_id: isDefault ? null : pg.id })}
                                className={`px-1.5 py-0.5 rounded text-[9px] font-medium transition-all ${isDefault ? 'bg-primary text-primary-foreground' : 'bg-muted/40 text-muted-foreground hover:bg-muted/60'}`}
                              >
                                {isDefault ? t('Padrao') : t('Definir')}
                              </button>
                              <button
                                onClick={() => handleOpenLinkGroups(pg.id, p.id)}
                                className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-muted/40 text-muted-foreground hover:bg-muted/60 transition-all"
                                title={t('Vincular Grupos')}
                              >
                                <Users className="h-3 w-3 inline mr-0.5" />{t('Grupos')}
                              </button>
                              <button
                                onClick={() => updatePage.mutate({ id: pg.id, is_active: !pg.isActive })}
                                className={`w-7 h-4 rounded-full transition-colors ${pg.isActive ? 'bg-green-500' : 'bg-zinc-600'}`}
                              >
                                <div className={`w-3 h-3 bg-white rounded-full transition-transform ${pg.isActive ? 'translate-x-3' : 'translate-x-0.5'}`} />
                              </button>
                              <button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all" onClick={() => { if (confirm(t('Desvincular?'))) deletePage.mutate(pg.id) }}>
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* Groups */}
                    {groups.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('Grupos')} ({groups.length})</p>
                        <div className="max-h-[140px] overflow-y-auto space-y-0.5">
                          {groups.map(g => (
                            <div key={g.id} className="flex items-center gap-2 py-0.5 group text-[11px]">
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${g.isActive ? 'bg-green-500' : 'bg-zinc-500'}`} />
                              <span className="truncate flex-1">{g.name}</span>
                              {g.collection && <Badge variant="outline" className="text-[8px] px-1 py-0 rounded border-border/40">{g.collection}</Badge>}
                              <button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all" onClick={() => { if (confirm(t('Remover grupo?'))) deleteGroup.mutate(g.id) }}>
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {pages.length === 0 && groups.length === 0 && (
                      <p className="text-[10px] text-muted-foreground py-1">{t('Nenhuma pagina ou grupo vinculado.')}</p>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}

          {/* Unassigned groups (only if not searching) */}
          {unassignedGroups.length > 0 && (
            <Collapsible>
              <div className="rounded-xl border border-dashed border-border/40 bg-muted/10">
                <CollapsibleTrigger className="flex items-center gap-2 px-3 py-2 w-full text-left">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{t('Sem perfil')} ({unassignedGroups.length} {t('grupos')})</span>
                  <ChevronDown className="h-3 w-3 text-muted-foreground ml-auto" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="border-t border-border/30 px-3 py-2 max-h-[140px] overflow-y-auto space-y-0.5">
                    {unassignedGroups.map(g => (
                      <div key={g.id} className="flex items-center gap-2 py-0.5 group text-[11px]">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${g.isActive ? 'bg-green-500' : 'bg-zinc-500'}`} />
                        <span className="truncate flex-1">{g.name}</span>
                        {g.collection && <Badge variant="outline" className="text-[8px] px-1 py-0 rounded border-border/40">{g.collection}</Badge>}
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}
        </div>
      )}

      {/* Create Profile Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="text-base">{t('Novo Perfil')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder={t('Nome do perfil (ex: Conta Joao)')} value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreate()} className="rounded-lg border-border/40" />
            <Button onClick={handleCreate} disabled={createProfile.isPending} className="w-full rounded-lg">
              {createProfile.isPending ? t('Criando...') : t('Criar Perfil')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={!!renameId} onOpenChange={() => setRenameId(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="text-base">{t('Renomear Perfil')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={renameName} onChange={e => setRenameName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleRename()} className="rounded-lg border-border/40" />
            <Button onClick={handleRename} disabled={updateProfile.isPending} className="w-full rounded-lg">
              {updateProfile.isPending ? t('Salvando...') : t('Salvar')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link Page Dialog (multi-select) */}
      <Dialog open={linkPageOpen} onOpenChange={setLinkPageOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="text-base">{t('Vincular Paginas a Perfil')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">{t('Perfil Chrome')}</Label>
              <Select value={linkPageProfileId || '_none'} onValueChange={(v) => setLinkPageProfileId(v === '_none' ? '' : v)}>
                <SelectTrigger className="rounded-lg border-border/40"><SelectValue placeholder={t('Selecione um perfil...')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">{t('Selecione...')}</SelectItem>
                  {profiles.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Paginas')}</Label>
                {unlinkedFinancePages.length > 0 && (
                  <Button variant="ghost" size="sm" className="h-5 text-[10px] rounded-lg px-2" onClick={() => setLinkPageFinanceIds(linkPageFinanceIds.length === unlinkedFinancePages.length ? [] : unlinkedFinancePages.map(fp => fp.id))}>
                    {linkPageFinanceIds.length === unlinkedFinancePages.length ? t('Desmarcar') : t('Todas')}
                  </Button>
                )}
              </div>
              {unlinkedFinancePages.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">{t('Todas as paginas ja estao vinculadas')}</p>
              ) : (
                <div className="max-h-[240px] overflow-y-auto rounded-xl border border-border/40 divide-y divide-border/30">
                  {unlinkedFinancePages.map(fp => (
                    <label key={fp.id} className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${linkPageFinanceIds.includes(fp.id) ? 'bg-primary/10' : 'hover:bg-muted/30'}`}>
                      <Checkbox
                        checked={linkPageFinanceIds.includes(fp.id)}
                        onCheckedChange={() => toggleLinkPage(fp.id)}
                      />
                      <span className="text-xs flex-1 truncate">{fp.name}</span>
                      {fp.followerCount && <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{fp.followerCount}</span>}
                    </label>
                  ))}
                </div>
              )}
              {linkPageFinanceIds.length > 0 && (
                <p className="text-[10px] text-muted-foreground mt-1 tabular-nums">{linkPageFinanceIds.length} {t('selecionada(s)')}</p>
              )}
            </div>
            <Button onClick={handleLinkPages} disabled={linkingPages || linkPageFinanceIds.length === 0 || !linkPageProfileId} className="w-full rounded-lg">
              {linkingPages ? t('Vinculando...') : t(`Vincular ${linkPageFinanceIds.length} Pagina(s)`)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Groups Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="text-base">{t('Importar Grupos em Massa')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea placeholder={t('Cole as URLs dos grupos, uma por linha')} value={importText} onChange={e => setImportText(e.target.value)} rows={8} className="rounded-lg border-border/40" />
            <Input placeholder={t('Colecao (opcional)')} value={importCollection} onChange={e => setImportCollection(e.target.value)} className="rounded-lg border-border/40" />
            <div>
              <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">{t('Perfil')}</Label>
              <Select value={importProfileId || '_none'} onValueChange={(v) => setImportProfileId(v === '_none' ? '' : v)}>
                <SelectTrigger className="rounded-lg border-border/40"><SelectValue placeholder={t('Selecione um perfil')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">{t('Sem perfil')}</SelectItem>
                  {profiles.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-muted-foreground tabular-nums">{importText.split('\n').filter(l => l.trim()).length} URLs detectadas</div>
            <Button onClick={handleImport} disabled={importGroups.isPending} className="w-full rounded-lg">
              {importGroups.isPending ? t('Importando...') : t('Importar')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link Groups to Page Dialog */}
      <Dialog open={linkGroupsOpen} onOpenChange={(open) => { if (!open) handleCloseLinkGroups() }}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">{t('Vincular Grupos a Pagina')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {t('Selecione quais grupos esta pagina pode postar. Ao postar como esta pagina, somente estes grupos aparecerão.')}
            </p>
            {loadingPageGroups ? (
              <Skeleton className="h-32 w-full rounded-xl" />
            ) : linkGroupsProfileGroups.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">{t('Nenhum grupo neste perfil.')}</p>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground tabular-nums">{linkGroupsSelected.length}/{linkGroupsProfileGroups.length} {t('selecionados')}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 text-[10px] rounded-lg px-2"
                    onClick={() => setLinkGroupsSelected(
                      linkGroupsSelected.length === linkGroupsProfileGroups.length
                        ? []
                        : linkGroupsProfileGroups.map(g => g.id),
                    )}
                  >
                    {linkGroupsSelected.length === linkGroupsProfileGroups.length ? t('Desmarcar') : t('Todos')}
                  </Button>
                </div>
                <div className="max-h-[280px] overflow-y-auto rounded-xl border border-border/40 divide-y divide-border/30">
                  {linkGroupsProfileGroups.map(g => (
                    <label
                      key={g.id}
                      className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${linkGroupsSelected.includes(g.id) ? 'bg-primary/10' : 'hover:bg-muted/30'}`}
                    >
                      <Checkbox
                        checked={linkGroupsSelected.includes(g.id)}
                        onCheckedChange={() => toggleLinkGroup(g.id)}
                      />
                      <span className="text-xs flex-1 truncate">{g.name}</span>
                      {g.collection && (
                        <Badge variant="outline" className="text-[8px] px-1 py-0 rounded border-border/40 shrink-0">{g.collection}</Badge>
                      )}
                    </label>
                  ))}
                </div>
              </>
            )}
            <Button
              onClick={handleSaveLinkGroups}
              disabled={syncPageGroups.isPending || linkGroupsProfileGroups.length === 0}
              className="w-full rounded-lg"
            >
              {syncPageGroups.isPending ? t('Salvando...') : t(`Salvar ${linkGroupsSelected.length} Grupo(s)`)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
